import { useState } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SignedIn, SignedOut, useUser, useClerk } from '@clerk/clerk-react';
import LangToggle from './LangToggle';
import Button from '@/ui/atoms/Button';
import ConfirmDialog from '@/ui/molecules/ConfirmDialog';

// PR-UX-10 confirm-undo · sign out is destructive (loses local cache + auth);
// gated behind an explicit confirmation, default focus on Cancel.
// PR-UX-01 70/30 · marketing nav at left ~30%, primary CTA at right ~30%, breathing space mid.
export default function Topbar() {
  const { t } = useTranslation();
  const { user } = useUser();
  const { signOut } = useClerk();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const initial = (
    user?.primaryEmailAddress?.emailAddress?.[0] ||
    user?.firstName?.[0] ||
    'T'
  ).toUpperCase();

  const onSignOutConfirm = async () => {
    setSigningOut(true);
    await signOut({ redirectUrl: '/' });
  };

  return (
    <>
      <header className="sticky top-0 z-50 bg-canvas/90 backdrop-blur border-b border-hairline">
        <div className="max-w-page mx-auto px-8 lg:px-16 xl:px-24 h-16 flex items-center justify-between">
          <SignedOut>
            <Link
              to="/"
              className="flex items-baseline gap-2 font-serif text-[28px] font-semibold tracking-tight"
            >
              <span className="text-vermilion text-[22px]">◆</span>
              <span>{t('brand')}</span>
            </Link>
          </SignedOut>
          <SignedIn>
            <Link
              to="/dashboard"
              className="flex items-baseline gap-2 font-serif text-[28px] font-semibold tracking-tight"
            >
              <span className="text-vermilion text-[22px]">◆</span>
              <span>{t('brand')}</span>
            </Link>
          </SignedIn>

          <div className="flex items-center gap-6">
            <SignedOut>
              <nav className="hidden md:flex gap-6 text-sm">
                <NavLink
                  to="/"
                  end
                  className={({ isActive }) =>
                    isActive ? 'text-ink font-medium' : 'text-ink-2 hover:text-ink transition'
                  }
                >
                  {t('nav.home')}
                </NavLink>
                <NavLink
                  to="/pricing"
                  className={({ isActive }) =>
                    isActive ? 'text-ink font-medium' : 'text-ink-2 hover:text-ink transition'
                  }
                >
                  {t('nav.pricing')}
                </NavLink>
              </nav>
              <LangToggle />
              <Link to="/auth" className="btn btn-primary">
                {t('nav.startFree')}
              </Link>
            </SignedOut>

            <SignedIn>
              <nav className="hidden md:flex gap-6 text-sm">
                <NavLink
                  to="/dashboard"
                  className={({ isActive }) =>
                    isActive ? 'text-ink font-medium' : 'text-ink-2 hover:text-ink transition'
                  }
                >
                  {t('nav.dashboard')}
                </NavLink>
                <NavLink
                  to="/how-it-works"
                  className={({ isActive }) =>
                    isActive ? 'text-ink font-medium' : 'text-ink-2 hover:text-ink transition'
                  }
                >
                  {t('nav.howItWorks')}
                </NavLink>
              </nav>
              <LangToggle />
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setConfirmOpen(true)}
                className="!px-2"
              >
                {t('nav.signOut')}
              </Button>
              <Link
                to="/account"
                className="w-9 h-9 bg-ink text-ink-inv rounded-full flex items-center justify-center font-serif text-sm font-medium hover:scale-105 transition"
                aria-label={t('nav.account')}
              >
                {initial}
              </Link>
            </SignedIn>
          </div>
        </div>
      </header>

      <ConfirmDialog
        open={confirmOpen}
        title={t('confirm.signOut.title')}
        body={t('confirm.signOut.body')}
        confirmLabel={t('confirm.signOut.confirm')}
        cancelLabel={t('confirm.signOut.cancel')}
        destructive
        onConfirm={onSignOutConfirm}
        onCancel={() => setConfirmOpen(false)}
        busy={signingOut}
      />
    </>
  );
}
